import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, CopyBox, Field, Modal, Spinner } from "../components/ui";
import {
  api,
  errorMessage,
  type SyncRepo,
  type Member,
  type PendingInvite,
  type Project,
  type RemoteInfo,
  type Role,
  type TreeEntry,
  type UserSettings,
  type WordCount,
  type WordCountRow,
} from "../lib/api";
import { fileList, formatDate, timeAgo, toast, useLocalStorage, useSession } from "../lib/app";

// ---------------------------------------------------------------- version control & sync

interface SyncOutcome {
  committed: string | null;
  pushed?: boolean;
  pulled?: number;
  status: string;
  /** Files where benleaf's version beat changes made elsewhere (admins only). */
  kept?: string[];
  error?: string;
}

export function SyncDialog({
  project,
  role,
  canManageSync,
  onClose,
  onProject,
}: {
  project: Project;
  role: Role;
  canManageSync: boolean;
  onClose: () => void;
  onProject: (p: Project) => void;
}) {
  const { user } = useSession();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [dirty, setDirty] = useState<boolean | null>(null);
  const [remote, setRemote] = useState<RemoteInfo | null>(null);
  const [repos, setRepos] = useState<SyncRepo[] | null>(null);
  // The server puts benleaf- on the front of every repository it creates.
  const [newName, setNewName] = useState("benleaf-" + project.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""));
  const [linkRepo, setLinkRepo] = useState("");
  const s = project.syncState;
  const canEdit = role !== "viewer";
  const isOwner = role === "owner";
  // Choosing where a project syncs to is admin-only (the server enforces this too).
  const manage = isOwner && canManageSync;

  const refresh = () =>
    api
      .get<{ project: Project; dirty: boolean; remote: RemoteInfo | null }>(`/api/projects/${project.id}/sync`)
      .then((r) => {
        setDirty(r.dirty);
        setRemote(r.remote);
        onProject(r.project);
      })
      .catch(() => {});
  useEffect(() => {
    refresh();
    // The check can pull (and merge) on its own, so reload everything once it's done.
    if (project.linked) api.post(`/api/projects/${project.id}/sync/fetch`).then(refresh).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!project.linked && manage && repos === null) {
      api
        .get<{ repos: SyncRepo[] }>("/api/sync/repos")
        .then((r) => setRepos(r.repos.filter((x) => x.empty)))
        .catch(() => setRepos([]));
    }
  }, [project.linked, manage, repos]);

  async function act(name: string, fn: () => Promise<{ outcome?: SyncOutcome; project?: Project }>) {
    setBusy(name);
    try {
      const res = await fn();
      if (res.project) onProject(res.project);
      const o = res.outcome;
      if (o?.status === "error") toast(o.error ?? "Sync failed", "error");
      else if (o) {
        const bits = [
          o.committed ? "committed" : null,
          o.pulled ? `pulled ${o.pulled} file(s)` : null,
          o.kept?.length ? `kept benleaf's version of ${fileList(o.kept)}` : null,
          o.pushed ? "synced" : null,
        ].filter(Boolean);
        toast(bits.length ? `Done: ${bits.join(", ")}` : "Already up to date", "success");
      }
      setMessage("");
      await refresh();
    } catch (e) {
      toast(errorMessage(e), "error");
    } finally {
      setBusy(null);
    }
  }

  const statusChip =
    s.status === "conflict" ? (
      <span className="chip chip-bad">
        <span className="dot" /> conflict
      </span>
    ) : s.status === "error" ? (
      <span className="chip chip-bad">
        <span className="dot" /> error
      </span>
    ) : (
      <span className="chip chip-ok">
        <span className="dot" /> in sync
      </span>
    );

  return (
    <Modal title={project.linked ? "Sync" : "Version control"} onClose={onClose} width={540}>
      <div className="stack">
        {project.linked ? (
          <>
            <div className="sync-status">
              <div className="grow">
                <div className="row">
                  {remote ? (
                    <>
                      <a className="mono" href={remote.url} target="_blank" rel="noreferrer">
                        {remote.name}
                      </a>
                      <span className="muted small">({remote.branch})</span>
                    </>
                  ) : (
                    <span>Syncing with the server</span>
                  )}
                  {statusChip}
                </div>
                <div className="muted small" style={{ marginTop: 4 }}>
                  {s.lastPushAt ? `Last synced ${timeAgo(s.lastPushAt)}` : "Not synced yet"}
                  {s.lastFetchAt ? ` · checked ${timeAgo(s.lastFetchAt)}` : ""}
                  {project.autoSync ? " · auto-sync on" : " · auto-sync off"}
                </div>
              </div>
            </div>
            {s.status === "conflict" && (
              <div className="alert alert-bad">
                {remote?.lastError ?? s.lastError}{" "}
                {remote?.conflictBranch && (
                  <a href={`${remote.url}/compare/${remote.branch}...${remote.conflictBranch}`} target="_blank" rel="noreferrer">
                    Open a pull request
                  </a>
                )}
              </div>
            )}
            {s.status === "error" && <div className="alert alert-bad">{remote?.lastError ?? s.lastError}</div>}
            {remote?.kept && (
              <div className="muted small" title={remote.kept.files.join("\n")}>
                Last clash {timeAgo(remote.kept.at)}: {fileList(remote.kept.files)} changed both here and elsewhere, and benleaf's version was kept. The
                other version is still in the history.
              </div>
            )}
            {!!s.behind && s.status !== "conflict" && <div className="alert alert-warn">The server has {s.behind} new change(s).</div>}
          </>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            Every project keeps its own version history. Edits are committed automatically after a few quiet minutes
            {manage ? "; set up syncing to send them to the server too." : "."}
          </p>
        )}

        {canEdit && (
          <Field label={project.linked ? "Commit & sync now" : "Commit now"} hint={dirty === false ? "No uncommitted changes." : dirty ? "There are uncommitted changes." : undefined}>
            <textarea rows={2} placeholder="What changed? (optional)" value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
        )}
        {canEdit && (
          <div className="row" style={{ justifyContent: "flex-end" }}>
            {project.linked && (
              <button className="btn" disabled={!!busy} onClick={() => act("pull", () => api.post(`/api/projects/${project.id}/sync/pull`))}>
                {busy === "pull" ? <Spinner /> : "Pull from server"}
              </button>
            )}
            <button className="btn btn-primary" disabled={!!busy} onClick={() => act("commit", () => api.post(`/api/projects/${project.id}/sync/commit`, { message }))}>
              {busy === "commit" ? <Spinner /> : project.linked ? "Commit & sync" : "Commit"}
            </button>
          </div>
        )}

        {isOwner && project.linked && (
          <div className="row" style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
            <label className="check grow">
              <input
                type="checkbox"
                checked={project.autoSync}
                onChange={async (e) => {
                  const r = await api.patch<{ project: Project }>(`/api/projects/${project.id}`, { autoSync: e.target.checked });
                  onProject(r.project);
                }}
              />
              <span>
                Auto-sync
                <div className="muted small">Send edits to the server once they settle, and pull in changes from the server while the project is open.</div>
              </span>
            </label>
            {manage && (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (confirm("Stop syncing with the server? The copy on the server is not changed.")) act("unlink", () => api.post(`/api/projects/${project.id}/sync/unlink`));
                }}
              >
                Unlink
              </button>
            )}
          </div>
        )}

        {isOwner && !project.linked && user?.isAdmin && (
          <div className="stack" style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
            {!canManageSync ? (
              <div className="alert alert-warn">Only the admin who owns the sync account can set up syncing.</div>
            ) : (
              <>
                <Field label="Create a new private repository" hint="Created on the sync server and sent straight away.">
                  <div className="row">
                    <input value={newName} onChange={(e) => setNewName(e.target.value)} />
                    <button
                      className="btn btn-primary"
                      disabled={!newName || !!busy}
                      onClick={() => act("create", () => api.post(`/api/projects/${project.id}/sync/create-repo`, { name: newName, private: true }))}
                    >
                      {busy === "create" ? <Spinner /> : "Create & push"}
                    </button>
                  </div>
                </Field>
                <Field label="…or link an existing empty repository">
                  <div className="row">
                    <select value={linkRepo} onChange={(e) => setLinkRepo(e.target.value)}>
                      <option value="">{repos === null ? "Loading…" : repos.length ? "Choose an empty repository" : "No empty repositories"}</option>
                      {repos?.map((r) => (
                        <option key={r.fullName} value={r.fullName}>
                          {r.fullName}
                        </option>
                      ))}
                    </select>
                    <button className="btn" disabled={!linkRepo || !!busy} onClick={() => act("link", () => api.post(`/api/projects/${project.id}/sync/link`, { repo: linkRepo }))}>
                      Link
                    </button>
                  </div>
                </Field>
                <p className="muted small" style={{ margin: 0 }}>
                  To work on a repository that already has content, use <b>New project → Import from server</b> on the dashboard.
                </p>
              </>
            )}
          </div>
        )}
        {s.lastCommitAt && <div className="muted small">Last commit {formatDate(s.lastCommitAt)}</div>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- sharing

export interface Sharing {
  members: Member[];
  pendingInvites: PendingInvite[];
}

export function ShareDialog({
  projectId,
  role,
  members,
  pendingInvites,
  onSharing,
  onClose,
}: {
  projectId: string;
  role: Role;
  members: Member[];
  pendingInvites: PendingInvite[];
  onSharing: (s: Sharing) => void;
  onClose: () => void;
}) {
  const { user } = useSession();
  const [users, setUsers] = useState<Array<{ id: number; username: string; displayName: string }>>([]);
  const [openInvites, setOpenInvites] = useState<Array<{ id: string; name: string }>>([]);
  const [invitesLeft, setInvitesLeft] = useState<number | null>(null);
  const [inviteLimit, setInviteLimit] = useState(0);
  const [pick, setPick] = useState("");
  const [newRole, setNewRole] = useState<Role>("editor");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("editor");
  const [link, setLink] = useState<{ name: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const isOwner = role === "owner";
  const isAdmin = !!user?.isAdmin;
  const canInvite = !!user?.canInvite;
  const mine = (p: PendingInvite) => isAdmin || p.createdBy === user?.id;

  useEffect(() => {
    api
      .get<{ users: typeof users; invites: typeof openInvites; invitesLeft: number | null; inviteLimit: number }>("/api/users")
      .then((r) => {
        setUsers(r.users);
        setOpenInvites(r.invites ?? []);
        setInvitesLeft(r.invitesLeft);
        setInviteLimit(r.inviteLimit);
      })
      .catch(() => {});
  }, [pendingInvites.length]);

  const candidates = users.filter((u) => !members.some((m) => m.id === u.id));
  const invitedElsewhere = openInvites.filter((i) => !pendingInvites.some((p) => p.id === i.id));

  async function run(fn: () => Promise<Sharing & { url?: string }>, linkFor?: string) {
    setBusy(true);
    try {
      const r = await fn();
      onSharing({ members: r.members, pendingInvites: r.pendingInvites });
      if (r.url && linkFor) setLink({ name: linkFor, url: r.url });
    } catch (e) {
      toast(errorMessage(e), "error");
    } finally {
      setBusy(false);
    }
  }

  const add = () => {
    const [kind, value] = [pick.slice(0, 2), pick.slice(2)];
    return run(() =>
      api.post<Sharing>(`/api/projects/${projectId}/members`, kind === "i:" ? { inviteId: value, role: newRole } : { username: value, role: newRole }),
    ).then(() => setPick(""));
  };

  const invite = () =>
    run(() => api.post<Sharing & { url: string }>(`/api/projects/${projectId}/invites`, { name: inviteName.trim(), role: inviteRole }), inviteName.trim()).then(
      () => setInviteName(""),
    );

  const changeMember = (m: Member, r: Role | null) =>
    run(() =>
      r === null
        ? api.del<Sharing>(`/api/projects/${projectId}/members/${m.id}`)
        : api.post<Sharing>(`/api/projects/${projectId}/members`, { username: m.username, role: r }),
    );

  return (
    <Modal title="Share project" onClose={onClose} width={540}>
      <div className="stack">
        {isOwner && (
          <div className="row">
            <select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">{candidates.length || invitedElsewhere.length ? "Add someone…" : "Everyone with an account already has access"}</option>
              {candidates.map((u) => (
                <option key={u.id} value={`u:${u.username}`}>
                  {u.displayName} ({u.username})
                </option>
              ))}
              {invitedElsewhere.map((i) => (
                <option key={i.id} value={`i:${i.id}`}>
                  {i.name} (invited, no account yet)
                </option>
              ))}
            </select>
            <select value={newRole} onChange={(e) => setNewRole(e.target.value as Role)} style={{ width: 110 }}>
              <option value="editor">Can edit</option>
              <option value="viewer">Can view</option>
            </select>
            <button className="btn btn-primary" disabled={!pick || busy} onClick={add}>
              Add
            </button>
          </div>
        )}

        {isOwner && canInvite && (
          <Field
            label="Invite someone who doesn't have an account yet"
            hint={
              isAdmin
                ? "They get a sign-up link and can start working here as soon as their account is set up."
                : `They get a sign-up link and can work on projects you share with them, but can't create their own until an admin verifies them. ${
                    invitesLeft === 0
                      ? `You've used all ${inviteLimit} of your invites; ask an admin to reset your limit.`
                      : `You can invite ${invitesLeft} more ${invitesLeft === 1 ? "person" : "people"}.`
                  }`
            }
          >
            <div className="row">
              <input
                placeholder="Who is this? (their name)"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && inviteName.trim() && !busy && invite()}
              />
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)} style={{ width: 110 }}>
                <option value="editor">Can edit</option>
                <option value="viewer">Can view</option>
              </select>
              <button className="btn" disabled={!inviteName.trim() || busy || invitesLeft === 0} onClick={invite}>
                Create link
              </button>
            </div>
          </Field>
        )}

        {link && (
          <div className="sync-status">
            <div className="grow stack" style={{ gap: 8 }}>
              <div>
                Send this link to <b>{link.name}</b>. It works once and expires in 7 days.
              </div>
              <CopyBox value={link.url} />
            </div>
          </div>
        )}

        <div>
          {members.map((m) => (
            <div key={m.id} className="row" style={{ padding: "8px 0", borderTop: "1px solid var(--line-soft)" }}>
              <Avatar id={m.id} name={m.displayName} size={26} />
              <div className="grow">
                <div>
                  {m.displayName} {m.id === user?.id && <span className="muted">(you)</span>}
                </div>
                <div className="muted small">{m.username}</div>
              </div>
              {m.role === "owner" ? (
                <span className="chip">Owner</span>
              ) : isOwner ? (
                <>
                  <select value={m.role} onChange={(e) => changeMember(m, e.target.value as Role)} style={{ width: 110 }}>
                    <option value="editor">Can edit</option>
                    <option value="viewer">Can view</option>
                  </select>
                  <button className="icon-btn" title="Remove" onClick={() => changeMember(m, null)}>
                    ×
                  </button>
                </>
              ) : (
                <span className="chip">{m.role === "editor" ? "Can edit" : "Can view"}</span>
              )}
            </div>
          ))}
          {pendingInvites.map((p) => (
            <div key={p.id} className="row" style={{ padding: "8px 0", borderTop: "1px solid var(--line-soft)" }}>
              <Avatar id={p.id} name={p.name} size={26} />
              <div className="grow">
                <div>{p.name}</div>
                <div className="small">
                  {p.expired ? (
                    <span className="error-text">Invite expired. Make a new link.</span>
                  ) : (
                    <span className="muted">Invited, hasn't created an account yet</span>
                  )}
                </div>
              </div>
              <select
                value={p.role}
                disabled={!mine(p)}
                onChange={(e) => run(() => api.post<Sharing>(`/api/projects/${projectId}/members`, { inviteId: p.id, role: e.target.value }))}
                style={{ width: 110 }}
              >
                <option value="editor">Can edit</option>
                <option value="viewer">Can view</option>
              </select>
              {canInvite && mine(p) && (
                <button
                  className="btn btn-sm"
                  title="Make a fresh link (the old one stops working)"
                  disabled={busy}
                  onClick={() => run(() => api.post<Sharing & { url: string }>(`/api/projects/${projectId}/invites/${p.id}/renew`), p.name)}
                >
                  New link
                </button>
              )}
              <button
                className="icon-btn"
                title="Remove from this project"
                onClick={() => run(() => api.del<Sharing>(`/api/projects/${projectId}/invites/${p.id}`))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          {canInvite
            ? "People you invite here can only see the projects you add them to."
            : "Only an admin or a trusted user can invite people who don't have a benleaf account yet."}
        </p>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- settings

export function SettingsDialog({
  project,
  role,
  tree,
  settings,
  onSettings,
  onProject,
  onClose,
  onClearCache,
}: {
  project: Project;
  role: Role;
  tree: TreeEntry[];
  settings: UserSettings;
  onSettings: (s: Partial<UserSettings>) => void;
  onProject: (p: Project) => void;
  onClose: () => void;
  onClearCache: () => void;
}) {
  const canEdit = role !== "viewer";
  const isOwner = role === "owner";
  const texFiles = tree.filter((e) => e.type === "file" && /\.tex$/i.test(e.path)).map((e) => e.path);
  async function patch(body: Partial<Project>) {
    try {
      const r = await api.patch<{ project: Project }>(`/api/projects/${project.id}`, body);
      onProject(r.project);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }
  return (
    <Modal title="Settings" onClose={onClose} width={520}>
      <div className="stack">
        <h3 style={{ fontSize: 15 }}>Project</h3>
        <Field label="Main document">
          <select disabled={!canEdit} value={project.rootDoc} onChange={(e) => patch({ rootDoc: e.target.value })}>
            {!texFiles.includes(project.rootDoc) && <option value={project.rootDoc}>{project.rootDoc} (missing)</option>}
            {texFiles.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Compiler">
          <select disabled={!canEdit} value={project.compiler} onChange={(e) => patch({ compiler: e.target.value as Project["compiler"] })}>
            <option value="pdflatex">pdfLaTeX</option>
            <option value="xelatex">XeLaTeX</option>
            <option value="lualatex">LuaLaTeX</option>
          </select>
        </Field>
        <label className="check">
          <input type="checkbox" disabled={!canEdit} checked={project.trackAll} onChange={(e) => patch({ trackAll: e.target.checked })} />
          <span>
            Track changes for everyone
            <div className="muted small">Every collaborator's edits become suggestions to accept or reject.</div>
          </span>
        </label>
        <div className="muted small">
          Compiles never run code from the document and can't reach the internet, so figures and data must be files in the project.
          Code listings (listings, minted), TikZ externalisation, EPS figures, bibliographies, glossaries and indexes all work.
        </div>
        {canEdit && (
          <div>
            <button className="btn btn-sm" onClick={onClearCache}>
              Clear compile cache
            </button>
          </div>
        )}

        <h3 style={{ fontSize: 15, marginTop: 8 }}>Editor (just you)</h3>
        <div className="row">
          <Field label="Theme">
            <select value={settings.theme ?? "system"} onChange={(e) => onSettings({ theme: e.target.value as UserSettings["theme"] })}>
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </Field>
          <Field label="Font size">
            <select value={settings.fontSize ?? 14} onChange={(e) => onSettings({ fontSize: Number(e.target.value) })}>
              {[11, 12, 13, 14, 15, 16, 18, 20].map((n) => (
                <option key={n} value={n}>
                  {n}px
                </option>
              ))}
            </select>
          </Field>
          <Field label="Keybindings">
            <select value={settings.keymap ?? "default"} onChange={(e) => onSettings({ keymap: e.target.value as UserSettings["keymap"] })}>
              <option value="default">Default</option>
              <option value="vim">Vim</option>
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="Spell check">
            <select value={settings.spellLang ?? "en-AU"} onChange={(e) => onSettings({ spellLang: e.target.value as UserSettings["spellLang"] })}>
              <option value="en-AU">English (Australia)</option>
              <option value="en-GB">English (UK)</option>
              <option value="en-US">English (US)</option>
              <option value="off">Off</option>
            </select>
          </Field>
          <Field label="Line wrapping">
            <select value={settings.lineWrap === false ? "off" : "on"} onChange={(e) => onSettings({ lineWrap: e.target.value === "on" })}>
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </Field>
        </div>
        {(settings.dictionary?.length ?? 0) > 0 && (
          <Field label={`Personal dictionary (${settings.dictionary!.length} words)`}>
            <div className="row" style={{ flexWrap: "wrap", gap: 4 }}>
              {settings.dictionary!.map((w) => (
                <span key={w} className="chip">
                  {w}
                  <button
                    className="icon-btn"
                    style={{ width: 16, height: 16, fontSize: 13 }}
                    onClick={() => onSettings({ dictionary: settings.dictionary!.filter((x) => x !== w) })}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </Field>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- word count

interface WordCountPrefs {
  /** Sections left out of the total, by kind:title (with #n for repeated titles). */
  excluded: string[];
  headers: boolean;
  captions: boolean;
  target: number | null;
}

const LEVELS: Record<string, number> = { part: 0, chapter: 1, section: 2 };
const levelOf = (kind: string) => LEVELS[kind.toLowerCase()] ?? 3;

export function WordCountDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [data, setData] = useState<WordCount | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useLocalStorage<WordCountPrefs>(`benleaf:wordcount:${projectId}`, { excluded: [], headers: true, captions: false, target: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<WordCount>(`/api/projects/${projectId}/wordcount`));
      setError("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => {
    load();
  }, [load]);

  const words = (r: WordCountRow) => r.text + (prefs.headers ? r.headers : 0) + (prefs.captions ? r.captions : 0);
  const rows = useMemo(() => {
    const excluded = new Set(prefs.excluded);
    const seen = new Map<string, number>();
    // Leaving out a chapter (or part) leaves out its sections too.
    const parents: Array<{ level: number; out: boolean }> = [];
    return (data?.sections ?? []).map((r) => {
      const base = `${r.kind}:${r.title}`;
      const n = (seen.get(base) ?? 0) + 1;
      seen.set(base, n);
      const key = n > 1 ? `${base}#${n}` : base;
      const level = r.kind ? levelOf(r.kind) : 0;
      while (parents.length && parents[parents.length - 1].level >= level) parents.pop();
      const inherited = parents.some((p) => p.out);
      const out = inherited || excluded.has(key);
      parents.push({ level, out });
      return { ...r, key, level, out, inherited };
    });
  }, [data, prefs.excluded]);
  const minLevel = Math.min(...rows.map((r) => r.level));
  const total = rows.length ? rows.filter((r) => !r.out).reduce((sum, r) => sum + words(r), 0) : data ? words(data.total) : 0;
  const leftOut = rows.filter((r) => r.out).length;
  const target = prefs.target && prefs.target > 0 ? prefs.target : null;
  const toggle = (key: string) =>
    setPrefs({ ...prefs, excluded: prefs.excluded.includes(key) ? prefs.excluded.filter((k) => k !== key) : [...prefs.excluded, key] });
  const counted = [prefs.headers ? "headings" : null, prefs.captions ? "captions" : null].filter(Boolean);

  return (
    <Modal
      title="Word count"
      onClose={onClose}
      width={560}
      footer={
        <>
          <span className="muted small grow">{data ? `Counted by texcount from ${data.rootDoc} and the files it includes, with everyone's latest edits.` : ""}</span>
          <button className="btn" disabled={loading} onClick={load}>
            {loading && data ? <Spinner /> : null} Recount
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {error ? (
        <p className="error-text">{error}</p>
      ) : !data ? (
        <div style={{ display: "grid", placeItems: "center", padding: 32 }}>
          <Spinner size={20} />
        </div>
      ) : (
        <div className="stack">
          <div className="wc-total">
            <div className="wc-number">{total.toLocaleString()}</div>
            <div className="muted small">
              words of body text{counted.length ? ` and ${counted.join(" and ")}` : ""}
              {leftOut ? `, leaving out ${leftOut} ${leftOut === 1 ? "section" : "sections"}` : ""}
            </div>
            {target && (
              <div className={`wc-bar${total > target ? " over" : ""}`} title={`${Math.round((total / target) * 100)}% of ${target.toLocaleString()}`}>
                <span style={{ width: `${Math.min(100, (total / target) * 100)}%` }} />
              </div>
            )}
            {target && (
              <div className={`small ${total > target ? "error-text" : "muted"}`}>
                {total > target ? `${(total - target).toLocaleString()} over` : `${(target - total).toLocaleString()} to go`} the {target.toLocaleString()}-word limit
              </div>
            )}
          </div>
          <div className="row wc-options">
            <label className="check">
              <input type="checkbox" checked={prefs.headers} onChange={(e) => setPrefs({ ...prefs, headers: e.target.checked })} />
              <span>Count headings</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={prefs.captions} onChange={(e) => setPrefs({ ...prefs, captions: e.target.checked })} />
              <span>Count captions</span>
            </label>
            <span className="grow" />
            <label className="row small muted">
              Word limit
              <input
                type="number"
                min={0}
                step={500}
                className="wc-target"
                placeholder="none"
                value={prefs.target ?? ""}
                onChange={(e) => setPrefs({ ...prefs, target: e.target.value ? Math.max(0, Number(e.target.value)) : null })}
              />
            </label>
          </div>
          {rows.length > 0 && (
            <div className="wc-sections">
              <div className="wc-head">
                <span className="grow">Untick sections to leave them out, like appendices</span>
                {leftOut > 0 && (
                  <button className="btn btn-sm btn-ghost" onClick={() => setPrefs({ ...prefs, excluded: [] })}>
                    Count all
                  </button>
                )}
              </div>
              {rows.map((r) => (
                <label key={r.key} className={`wc-row${r.out ? " out" : ""}`} style={{ paddingLeft: 10 + (r.level - minLevel) * 18 }}>
                  <input type="checkbox" checked={!r.out} disabled={r.inherited} onChange={() => toggle(r.key)} />
                  <span className="grow wc-title" title={r.kind ? `${r.kind}: ${r.title}` : r.title}>
                    {r.title}
                  </span>
                  <span className="wc-words">{words(r).toLocaleString()}</span>
                </label>
              ))}
            </div>
          )}
          <div className="muted small">
            Whole document: {data.total.text.toLocaleString()} words of text, {data.total.headers.toLocaleString()} in headings and{" "}
            {data.total.captions.toLocaleString()} in captions; {data.total.floats} figures and tables, {data.total.mathInline} inline and{" "}
            {data.total.mathDisplay} displayed equations.
          </div>
          {data.warnings.length > 0 && (
            <div className="small error-text">
              {data.warnings.map((w) => (
                <div key={w}>{w}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
